import React, { useState } from 'react';
import { Box, IconButton, ListItemIcon, Typography } from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { AcUnit } from '@mui/icons-material'; // Dummy icon for AC images
import {
  ICreateBooking,
  ICreateServiceDetails,
  IService,
  IServiceDetails,
} from '../../utils/entities/interface';
import { Images } from '../../assets';

export const acItems = [
  { name: 'SPLIT', icon: Images.ACICONSPLIT },
  { name: 'VRV/VRF', icon: Images.ACICONVRV_VRF },
  { name: 'WINDOW', icon: Images.ACICONWINDOW },
  { name: 'DUCTED', icon: Images.ACICONDUCTED },
  { name: 'CASSETTE', icon: Images.ACICONCASSETTE },
  { name: 'TOWER', icon: Images.ACICONTOWER },
  { name: 'CONCEALED', icon: Images.ACICONDUCTED },
];

export const ACItem = ({
  ac,
  initialCount,
  disabled,
  onCountChange,
}: {
  ac: { name: string; icon: any };
  initialCount: number;
  disabled?: boolean;
  onCountChange: (name: string, newCount: number) => void;
}) => {
  const [count, setCount] = useState(initialCount);

  const handleIncrement = () => {
    const newCount = count + 1;
    setCount(newCount);
    onCountChange(ac.name, newCount);
  };

  const handleDecrement = () => {
    const newCount = count - 1;
    setCount(newCount);
    onCountChange(ac.name, newCount);
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      mb={2}
      p={1}
      border={1}
      borderRadius={4}
      bgcolor="#1E1E1E"
      borderColor="rgba(255, 255, 255, 0.12)"
      sx={{
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        // bgcolor={disabled ? '#444' : '#555'}
        borderRadius="50%"
        width={50}
        height={50}
      >
        {/* <ListItemIcon  */}
        <ServiceIcon imageUrl={ac.icon} />
        {/* </ListItemIcon> */}
      </Box>
      <Typography flex={1} marginLeft={2} color={disabled ? '#AAA' : 'white'}>
        {ac.name + ' ' + 'AC'}
      </Typography>
      <Box display="flex" alignItems="center">
        <IconButton
          onClick={handleDecrement}
          disabled={count <= 0 || disabled}
          style={{ color: disabled ? '#AAA' : 'white' }}
        >
          <Remove />
        </IconButton>
        <Typography variant="body1" mx={2} color={disabled ? '#AAA' : 'white'}>
          {count}
        </Typography>
        <IconButton
          onClick={handleIncrement}
          disabled={disabled}
          style={{ color: disabled ? '#AAA' : 'white' }}
        >
          <Add />
        </IconButton>
      </Box>
    </Box>
  );
};

export const ACList = ({
  disabled,
  details,
  setDirtyBooking,
  service,
}: {
  disabled?: boolean;
  details: IServiceDetails[] | ICreateServiceDetails[] | undefined;
  setDirtyBooking: React.Dispatch<React.SetStateAction<ICreateBooking>>;
  service: IService | null;
}) => {
  const handleCountChange = (name: string, newCount: number) => {
    setDirtyBooking((prevBooking): ICreateBooking => {
      // Get all service IDs
      const serviceIds: string[] = prevBooking.serviceDetails.map(
        (ser) => ser.service_id,
      );

      // Filter the serviceDetails for the provided service_id
      const filteredServiceDetails = prevBooking.serviceDetails.filter(
        (detail) => detail.service_id === service?._id,
      );

      const updatedDetails: ICreateServiceDetails[] =
        filteredServiceDetails.map((detail) => {
          // Update only the detail that matches both acType and service_id
          if (
            detail.acType.toUpperCase() === name.toUpperCase() &&
            detail.service_id === service?._id
          ) {
            return { ...detail, quantity: String(newCount) }; // Update quantity for matching acType and service_id
          }
          return detail; // No update for other details
        });

      // Check if the AC type exists in the details array for the specific service_id
      const acTypeExists = updatedDetails.some(
        (detail) =>
          detail.acType.toUpperCase() === name.toUpperCase() &&
          detail.service_id === service?._id,
      );

      // If the AC type does not exist, add a new object to the details array with the correct service_id
      if (!acTypeExists) {
        updatedDetails.push({
          acType: name,
          quantity: String(newCount), // Quantity remains a string here
          services: [], // Default empty array for services
          serviceType: '', // Default empty string for serviceType
          place: '', // Default empty string for place
          comment: '', // Default empty string for comment
          service_id: service?._id as string, // Use the correct service_id
          otherService: '',
        });
      }

      // Merge the updated details with the previous serviceDetails that don't match the current service_id
      const mergedDetails = prevBooking.serviceDetails
        .filter((detail) => detail.service_id !== service?._id)
        .concat(updatedDetails);

      return { ...prevBooking, serviceDetails: mergedDetails };
    });
  };

  // Map acItems with details to get the initial count for each AC type
  const acItemsWithQuantity =
    Array.isArray(acItems) &&
    acItems.map((ac) => {
      const matchedDetail =
        Array.isArray(details) &&
        details.find(
          (detail) =>
            detail.acType.toUpperCase() === ac.name.toUpperCase() &&
            detail.service_id === service?._id,
        );
      return {
        ...ac,
        initialCount: matchedDetail ? matchedDetail.quantity : 0,
        icon: ac.icon,
      };
    });

  return (
    <>
      <Box display="flex" alignItems="center">
        {/* <Typography
          variant="h6"
          sx={{ marginLeft: 1, fontWeight: 'bold', color: 'white' }}
        >
          Select Type Of AC
        </Typography> */}
      </Box>
      <Box>
        {Array.isArray(acItemsWithQuantity) &&
          acItemsWithQuantity.map((ac, index) => (
            <ACItem
              key={index}
              ac={ac}
              initialCount={Number(ac.initialCount)}
              disabled={disabled}
              onCountChange={handleCountChange}
            />
          ))}
      </Box>
    </>
  );
};

const ServiceIcon = ({ imageUrl }: { imageUrl: string }) => {
  return (
    <img
      src={imageUrl}
      alt="Service Icon"
      style={{ width: '40px', height: '40px' }}
    />
  );
};
